import {
  ILabel,
  IVisibilityRule,
  IVisibilityCondition,
} from '../../constants/interfaces';

/**
 * Evaluates a single visibility condition
 */
export const evaluateCondition = (
  condition: IVisibilityCondition,
  labelResponses: Record<number, string>
): boolean => {
  const { label_id, value, operator = 'equals' } = condition;
  const currentValue = labelResponses[label_id] || '';

  switch (operator) {
    case 'equals':
      return currentValue === value;

    case 'not_equals':
      return currentValue !== value;

    case 'contains':
      if (typeof value === 'string') {
        return currentValue.toLowerCase().includes(value.toLowerCase());
      }
      return false;

    case 'in':
      if (Array.isArray(value)) {
        return value.includes(currentValue);
      }
      return false;

    case 'not_in':
      if (Array.isArray(value)) {
        return !value.includes(currentValue);
      }
      return true;

    case 'greater_than':
      const gtNum = parseFloat(currentValue);
      const gtTarget = parseFloat(value as string);
      return !isNaN(gtNum) && !isNaN(gtTarget) && gtNum > gtTarget;

    case 'less_than':
      const ltNum = parseFloat(currentValue);
      const ltTarget = parseFloat(value as string);
      return !isNaN(ltNum) && !isNaN(ltTarget) && ltNum < ltTarget;

    case 'greater_than_or_equal':
      const gteNum = parseFloat(currentValue);
      const gteTarget = parseFloat(value as string);
      return !isNaN(gteNum) && !isNaN(gteTarget) && gteNum >= gteTarget;

    case 'less_than_or_equal':
      const lteNum = parseFloat(currentValue);
      const lteTarget = parseFloat(value as string);
      return !isNaN(lteNum) && !isNaN(lteTarget) && lteNum <= lteTarget;

    default:
      return false;
  }
};

/**
 * Evaluates visibility rules for a label
 */
export const evaluateVisibilityRules = (
  label: ILabel,
  labelResponses: Record<number, string>
): boolean => {
  // If no visibility rules, the label is always visible
  if (!label.visibilityRules) {
    return true;
  }

  // Handle string-based rules (if they're JSON strings)
  let rules: IVisibilityRule;
  if (typeof label.visibilityRules === 'string') {
    try {
      rules = JSON.parse(label.visibilityRules);
    } catch (error) {
      console.warn(
        'Failed to parse visibility rules for label',
        label.labelId,
        error
      );
      return true; // Default to visible if parsing fails
    }
  } else {
    rules = label.visibilityRules;
  }

  // If no conditions, default to visible
  if (!rules.conditions || rules.conditions.length === 0) {
    return true;
  }

  // Evaluate conditions based on operator
  const operator = rules.operator || 'AND';
  const results = rules.conditions.map((condition) =>
    evaluateCondition(condition, labelResponses)
  );

  if (operator === 'OR') {
    return results.some((result) => result);
  } else {
    return results.every((result) => result);
  }
};

/**
 * Filters labels based on their visibility rules
 */
export const getVisibleLabels = (
  labels: ILabel[],
  labelResponses: Record<number, string>
): ILabel[] => {
  return labels.filter((label) =>
    evaluateVisibilityRules(label, labelResponses)
  );
};
